import React, { useState, useMemo } from 'react';
import { useAllPayments, UnifiedPayment } from '@/hooks/useAllPayments';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { DollarSign, CheckCircle2, Clock, AlertCircle, Search, Filter, ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDistanceToNow } from 'date-fns';

export const BrandPaymentsOverview: React.FC = () => {
  const { payments, loading, summary, refetch } = useAllPayments();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [payingTaskId, setPayingTaskId] = useState<string | null>(null);
  const [setAmountOpen, setSetAmountOpen] = useState(false);
  const [setAmountValue, setSetAmountValue] = useState('');
  const [setAmountTask, setSetAmountTask] = useState<UnifiedPayment | null>(null);
  const [expandedDeals, setExpandedDeals] = useState<Set<string>>(new Set());

  const toggleDeal = (dealId: string) => {
    setExpandedDeals(prev => {
      const next = new Set(prev);
      if (next.has(dealId)) {
        next.delete(dealId);
      } else {
        next.add(dealId);
      }
      return next;
    });
  };

  const expandAll = () => {
    const allDealIds = Object.keys(paymentsByDeal);
    setExpandedDeals(new Set(allDealIds));
  };

  const collapseAll = () => {
    setExpandedDeals(new Set());
  };

  const openSetAmountDialog = (payment: UnifiedPayment) => {
    setSetAmountTask(payment);
    setSetAmountValue('');
    setSetAmountOpen(true);
  };

  const handleSetAmount = async () => {
    if (!setAmountTask?.task_id || !setAmountValue) return;
    
    const amount = parseFloat(setAmountValue);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Invalid amount', { description: 'Please enter a valid amount greater than 0' });
      return;
    }
    
    try {
      const { error } = await supabase
        .from('deal_tasks')
        .update({ 
          payment_amount: amount,
          payment_status: 'due'
        })
        .eq('id', setAmountTask.task_id);

      if (error) throw error;

      toast.success('Payment amount set', {
        description: `Set $${amount.toFixed(2)} for ${setAmountTask.title}`
      });
      
      setSetAmountOpen(false);
      await refetch();
    } catch (error: any) {
      console.error('Set amount error:', error);
      toast.error('Failed to set amount', {
        description: error.message || 'Failed to update task'
      });
    }
  };

  const handlePayTask = async (payment: UnifiedPayment) => {
    if (payment.type !== 'task' || !payment.task_id) return;
    
    setPayingTaskId(payment.task_id);
    
    try {
      const { error } = await supabase.functions.invoke('pay-task', {
        body: { taskId: payment.task_id }
      });

      if (error) throw error;

      toast.success('Payment completed successfully', {
        description: `Paid $${payment.amount.toFixed(2)} for ${payment.title}`
      });
      
      await refetch();
    } catch (error: any) {
      console.error('Payment error:', error);
      toast.error('Payment failed', {
        description: error.message || 'Failed to process payment'
      });
    } finally {
      setPayingTaskId(null);
    }
  };

  const filteredPayments = useMemo(() => {
    return payments.filter(payment => {
      const matchesSearch = payment.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           payment.deal_title.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
      const matchesType = typeFilter === 'all' || payment.type === typeFilter;
      
      return matchesSearch && matchesStatus && matchesType;
    });
  }, [payments, searchQuery, statusFilter, typeFilter]);

  const paymentsByDeal = useMemo(() => {
    const grouped: Record<string, {
      dealId: string;
      dealTitle: string;
      payments: UnifiedPayment[];
    }> = {};
    
    filteredPayments.forEach(payment => {
      const dealId = payment.deal_id || 'no-deal';
      const dealTitle = payment.deal_title || 'Unassigned Payments';
      
      if (!grouped[dealId]) {
        grouped[dealId] = {
          dealId,
          dealTitle,
          payments: []
        };
      }
      
      grouped[dealId].payments.push(payment);
    });
    
    return grouped;
  }, [filteredPayments]);

  const dealMetrics = useMemo(() => {
    const metrics: Record<string, {
      total: number;
      paid: number;
      pending: number;
      overdue: number;
      totalAmount: number;
      paidAmount: number;
      pendingAmount: number;
      completionRate: number;
    }> = {};
    
    Object.entries(paymentsByDeal).forEach(([dealId, deal]) => {
      const total = deal.payments.length;
      const paid = deal.payments.filter(p => p.status === 'paid').length;
      const pending = deal.payments.filter(p => p.status === 'pending').length;
      const overdue = deal.payments.filter(p => p.status === 'overdue').length;
      
      const totalAmount = deal.payments.reduce((sum, p) => sum + p.amount, 0);
      const paidAmount = deal.payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0);
      const pendingAmount = deal.payments.filter(p => p.status !== 'paid').reduce((sum, p) => sum + p.amount, 0);
      
      const completionRate = total > 0 ? Math.round((paid / total) * 100) : 0;
      
      metrics[dealId] = {
        total,
        paid,
        pending,
        overdue,
        totalAmount,
        paidAmount,
        pendingAmount,
        completionRate
      };
    });
    
    return metrics;
  }, [paymentsByDeal]);

  const upcomingPayments = useMemo(() => {
    const today = new Date();
    const in7Days = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    return payments.filter(p => {
      const dueDate = new Date(p.due_date);
      return p.status !== 'paid' && dueDate >= today && dueDate <= in7Days;
    });
  }, [payments]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return <CheckCircle2 className="w-4 h-4" />;
      case 'overdue': return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20';
      case 'overdue': return 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20';
      default: return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20';
    }
  };

  const getRowColor = (payment: UnifiedPayment) => {
    if (payment.status === 'overdue') return 'border-l-4 border-l-red-500 bg-red-500/5';
    
    const today = new Date();
    const dueDate = new Date(payment.due_date);
    const in7Days = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    if (payment.status !== 'paid' && dueDate >= today && dueDate <= in7Days) {
      return 'border-l-4 border-l-yellow-500 bg-yellow-500/5';
    }
    
    if (payment.status === 'paid') return 'opacity-60';
    
    return '';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-20" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32 mb-2" />
                <Skeleton className="h-4 w-24" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Payments</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(summary.totalAmount)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {payments.length} total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              Paid
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(summary.paidAmount)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.paidCount} payments
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-yellow-600" />
              Pending
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{formatCurrency(summary.pendingAmount)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.pendingCount} payments
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600" />
              Overdue
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{formatCurrency(summary.overdueAmount)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.overdueCount} payments
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Payments Alert */}
      {upcomingPayments.length > 0 && (
        <Alert className="border-yellow-500/50 bg-yellow-500/10">
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <AlertDescription>
            <div className="font-semibold mb-2">
              {upcomingPayments.length} payment{upcomingPayments.length > 1 ? 's' : ''} due in the next 7 days ({formatCurrency(summary.upcomingIn7Days)})
            </div>
            <div className="space-y-1 text-sm">
              {upcomingPayments.slice(0, 3).map(payment => (
                <div key={payment.id} className="flex items-center justify-between">
                  <span>{payment.title} - {payment.deal_title}</span>
                  <span className="font-medium">{formatCurrency(payment.amount)}</span>
                </div>
              ))}
              {upcomingPayments.length > 3 && (
                <div className="text-xs text-muted-foreground">
                  +{upcomingPayments.length - 3} more
                </div>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Payments</CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={expandAll}
              >
                <ChevronDown className="w-4 h-4 mr-1" />
                Expand All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={collapseAll}
              >
                <ChevronUp className="w-4 h-4 mr-1" />
                Collapse All
              </Button>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search by title or deal..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="schedule">Schedules</SelectItem>
                <SelectItem value="task">Tasks</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {Object.keys(paymentsByDeal).length === 0 ? (
            <div className="text-center py-12 space-y-4">
              <DollarSign className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No payments found</h3>
              {searchQuery || statusFilter !== 'all' || typeFilter !== 'all' ? (
                <p className="text-muted-foreground">Try adjusting your filters</p>
              ) : (
                <div className="text-sm text-muted-foreground space-y-2">
                  <p>To see payment items here:</p>
                  <ul className="list-disc list-inside space-y-1 text-left max-w-md mx-auto">
                    <li>Mark tasks as completed in Timeline & Tasks</li>
                    <li>Set payment amounts for completed tasks using "Set Amount"</li>
                    <li>Create payment schedules for your deals</li>
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(paymentsByDeal)
                .sort(([, a], [, b]) => a.dealTitle.localeCompare(b.dealTitle))
                .map(([dealId, deal]) => {
                  const metrics = dealMetrics[dealId];
                  const isExpanded = expandedDeals.has(dealId);
                  
                  return (
                    <Collapsible
                      key={dealId}
                      open={isExpanded}
                      onOpenChange={() => toggleDeal(dealId)}
                    >
                      <div className="border rounded-lg bg-card">
                        <CollapsibleTrigger className="w-full p-4 hover:bg-accent/50 transition-colors">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {isExpanded ? (
                                <ChevronDown className="w-5 h-5 shrink-0 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="w-5 h-5 shrink-0 text-muted-foreground" />
                              )}
                              <div className="text-left flex-1 min-w-0">
                                <h3 className="font-semibold text-lg truncate">{deal.dealTitle}</h3>
                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                  <span className="text-sm text-muted-foreground">
                                    {metrics.total} payment{metrics.total !== 1 ? 's' : ''}
                                  </span>
                                  <span className="text-sm text-green-600">
                                    {metrics.paid} paid
                                  </span>
                                  {metrics.pending > 0 && (
                                    <span className="text-sm text-yellow-600">
                                      {metrics.pending} pending
                                    </span>
                                  )}
                                  {metrics.overdue > 0 && (
                                    <span className="text-sm text-red-600">
                                      {metrics.overdue} overdue
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-4 shrink-0">
                              <div className="text-right">
                                <div className="text-lg font-bold">{formatCurrency(metrics.totalAmount)}</div>
                                <div className="text-xs text-muted-foreground">
                                  {formatCurrency(metrics.paidAmount)} paid
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <div className="text-right">
                                  <div className="text-2xl font-bold">{metrics.completionRate}%</div>
                                  <div className="text-xs text-muted-foreground">complete</div>
                                </div>
                                <div className="w-12 h-12 relative">
                                  <svg className="w-12 h-12 transform -rotate-90">
                                    <circle
                                      cx="24"
                                      cy="24"
                                      r="20"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                      fill="none"
                                      className="text-muted"
                                    />
                                    <circle
                                      cx="24"
                                      cy="24"
                                      r="20"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                      fill="none"
                                      strokeDasharray={`${2 * Math.PI * 20}`}
                                      strokeDashoffset={`${2 * Math.PI * 20 * (1 - metrics.completionRate / 100)}`}
                                      className="text-green-600 transition-all duration-300"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        
                        <CollapsibleContent>
                          <div className="border-t">
                            <div className="p-4 space-y-2">
                              {deal.payments.map(payment => (
                                <div
                                  key={payment.id}
                                  className={`p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors ${getRowColor(payment)}`}
                                >
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <h4 className="font-semibold truncate">{payment.title}</h4>
                                        <Badge variant="outline" className="text-xs shrink-0">
                                          {payment.type === 'schedule' ? 'Schedule' : 'Task'}
                                        </Badge>
                                        
                                        {/* Task status badge for tasks */}
                                        {payment.type === 'task' && payment.task_status && (
                                          <Badge 
                                            variant="outline" 
                                            className={`text-xs shrink-0 ${
                                              payment.task_status === 'completed' 
                                                ? 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20'
                                                : payment.task_status === 'in_progress'
                                                ? 'bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/20'
                                                : 'bg-gray-500/10 text-gray-700 dark:text-gray-400 border-gray-500/20'
                                            }`}
                                          >
                                            {payment.task_status === 'in_progress' ? 'In Progress' : payment.task_status}
                                          </Badge>
                                        )}
                                        
                                        <Badge className={`${getStatusColor(payment.status)} shrink-0`}>
                                          <div className="flex items-center gap-1">
                                            {getStatusIcon(payment.status)}
                                            <span className="capitalize">{payment.status}</span>
                                          </div>
                                        </Badge>
                                      </div>
                                      <p className="text-xs text-muted-foreground mt-1">
                                        Due {formatDistanceToNow(new Date(payment.due_date), { addSuffix: true })}
                                      </p>
                                    </div>
                                    
                                    <div className="flex items-center gap-3 sm:flex-row-reverse">
                                      <div className="text-right shrink-0">
                                        <div className="text-lg font-bold">{formatCurrency(payment.amount)}</div>
                                        {payment.payment_date && (
                                          <p className="text-xs text-muted-foreground">
                                            Paid {formatDistanceToNow(new Date(payment.payment_date), { addSuffix: true })}
                                          </p>
                                        )}
                                      </div>
                                      
                                      {payment.status !== 'paid' && payment.type === 'task' && (
                                        payment.task_status === 'completed' ? (
                                          payment.amount > 0 ? (
                                            <Button
                                              size="sm"
                                              onClick={() => handlePayTask(payment)}
                                              disabled={payingTaskId === payment.task_id}
                                              className="shrink-0"
                                            >
                                              <DollarSign className="w-4 h-4 mr-1" />
                                              {payingTaskId === payment.task_id ? 'Processing...' : 'Pay Now'}
                                            </Button>
                                          ) : (
                                            <Button
                                              size="sm"
                                              variant="secondary"
                                              onClick={() => openSetAmountDialog(payment)}
                                              className="shrink-0"
                                            >
                                              Set Amount
                                            </Button>
                                          )
                                        ) : (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            disabled
                                            className="shrink-0"
                                          >
                                            <Clock className="w-4 h-4 mr-1" />
                                            Task {payment.task_status === 'in_progress' ? 'In Progress' : 'Pending'}
                                          </Button>
                                        )
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  );
                })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Set Amount Dialog */}
      <Dialog open={setAmountOpen} onOpenChange={setSetAmountOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Payment Amount</DialogTitle>
            <DialogDescription>
              Set the payment amount for: {setAmountTask?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Amount (USD)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  id="amount"
                  type="number"
                  placeholder="0.00"
                  value={setAmountValue}
                  onChange={(e) => setSetAmountValue(e.target.value)}
                  className="pl-9"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetAmountOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSetAmount}>
              Save Amount
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
